import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert } from '@vnetwork/ui/feedback';
import { Badge } from '@vnetwork/ui/primitives';
import { currentWorkspaceRoute, sameId, useOrgCustomerId, useOrgWorkspaces, useSharedProjects, useWorkingProject } from '@/api/iam';
import { WORKSPACE_SERVICES } from '@/lib/workspace-services';
import { homeOrgNameOf } from './project-switcher/model';
import {
  dismissNote,
  homeOrgNote,
  homeOrganizationName,
  isNoteDismissed,
  isServicePage,
  mobileOrgBadge,
  readNoteDismissals,
  workspaceDocumentTitle,
} from './home-org-note';
import styles from './WorkspaceHints.module.css';

/** The services whose workspaces the top bar loads; the same query keys, so reading them here costs no second fetch. */
const SWITCHABLE = WORKSPACE_SERVICES.filter((s) => s.switchable);

/** What the URL says about the workspace that is open: whose it is, which page, and whether it is a service page at all. */
function useOpenWorkspace() {
  const { pathname } = useLocation();
  const { workspaces } = useOrgWorkspaces(SWITCHABLE);
  return useMemo(() => {
    const current = currentWorkspaceRoute(SWITCHABLE, pathname, workspaces);
    const organizationId = current?.match.organizationId;
    return {
      organizationId,
      page: current?.match.page ?? '',
      pageLabel: current?.service.pageLabel?.(current.match.page),
      organizationName: organizationId ? workspaces.find((w) => sameId(w.organizationId, organizationId))?.organizationName : undefined,
      workspaces,
      // A workspace page or a service landing: Compute and Kubernetes pages never carry the home-organization note.
      onServicePage: isServicePage({ pathname, insideWorkspace: !!current, landings: WORKSPACE_SERVICES.map((s) => s.landing) }),
    };
  }, [pathname, workspaces]);
}

function session(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined; // reading the property itself throws when storage is blocked
  }
}

/** C9: closable, non-blocking note under the topbar on pages that always use the home organization. */
export function HomeOrgNote() {
  const { project, settled } = useWorkingProject();
  const home = useOrgCustomerId();
  const { data: shared = [] } = useSharedProjects();
  const { workspaces, onServicePage } = useOpenWorkspace();
  const [raw, setRaw] = useState<string | null>(() => readNoteDismissals(session()));
  const foreignId = project?.organizationId ?? '';
  const foreignName =
    shared.find((p) => sameId(p.customer_id, foreignId))?.org_name || workspaces.find((w) => sameId(w.organizationId, foreignId))?.organizationName;
  const text = homeOrgNote({
    stored: project,
    settled,
    home,
    homeName: homeOrganizationName({ fromSharedProjects: homeOrgNameOf(shared, home), workspaces, home }),
    foreignName,
    onWorkspaceRoute: onServicePage,
    dismissed: isNoteDismissed(raw, foreignId),
  });
  const dismiss = useCallback(() => {
    setRaw(dismissNote(session(), raw, foreignId));
  }, [raw, foreignId]);
  if (!text) return null;
  return (
    <Alert tone="info" role="status" className={styles.note} onDismiss={dismiss}>
      {text}
    </Alert>
  );
}

/** P1: below 768px, the Shared badge and the organization name while another organization's workspace is open. */
export function MobileOrgBadge() {
  const home = useOrgCustomerId();
  const { organizationId, organizationName } = useOpenWorkspace();
  const badge = mobileOrgBadge({ home, workspaceOrganizationId: organizationId, organizationName });
  if (!badge) return null;
  return (
    <div className={styles.mobileBadge} role="status">
      <Badge tone="info" appearance="subtle" size="sm">
        Shared
      </Badge>
      <span className={styles.mobileName} title={badge.organizationName}>
        {badge.organizationName}
      </span>
    </div>
  );
}

/** D2: `<page> · <Organization name>` as the document title while another organization's workspace is open. */
export function WorkspaceDocumentTitle() {
  const home = useOrgCustomerId();
  const { organizationId, page, pageLabel, organizationName } = useOpenWorkspace();
  const title = workspaceDocumentTitle({ home, workspaceOrganizationId: organizationId, page, pageLabel, organizationName });
  useEffect(() => {
    if (!title) return;
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);
  return null;
}
